import React from 'react';
import { Formik, Form, Field as FormikField } from 'formik';
import * as Yup from 'yup';

import { AuthShell } from '../../design-system/components/shells';
import {
  AuthBanner,
  AuthField,
  AuthFields,
  AuthFootnote,
  AuthHint,
  AuthPasswordInput,
  AuthStrength,
  AuthSubmit,
} from '../../design-system/patterns/auth';

/**
 * Reset password, the view. Pure: every input is a prop, every output is a
 * callback. `ResetPassword.jsx` owns the token, the request, the toasts and the
 * timed redirect.
 *
 * One view, four states, because the route really has four: the token is being
 * checked, the token is no good, the form, and the confirmation. They are the
 * same slot swapped, exactly as the template shows them, so they live in one
 * component behind a `status` prop rather than in four files.
 *
 * DELIBERATE DIFFERENCES FROM THE UPSTREAM TEMPLATE
 * -------------------------------------------------
 * 1. The template's reassurance line under the submit reads "You will be signed
 *    in straight after". This app does not sign you in; it sends you to the
 *    login screen with a message. The copy says what actually happens.
 *
 * 2. The template says reset links "last 30 minutes". The app cannot see the
 *    backend's window, so the copy does not name a number.
 *
 * 3. The template renders the submit disabled until both rules pass. Formik
 *    owns validity here and the app has always allowed a submit attempt that
 *    surfaces the field errors, so the button disables only while a request is
 *    in flight.
 *
 * 4. The validating state has no spinner component to use: the library ships no
 *    `Spinner` yet (see CONVERSION-MAP.md, cross-cutting deletions) and
 *    inventing one in a page would be exactly the duplication this conversion
 *    removes. It is a titled, polite live region until that component exists.
 */

export const resetPasswordValidationSchema = Yup.object({
  new_password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    )
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('new_password'), null], 'Passwords must match')
    .required('Please confirm your password'),
});

export function ResetPasswordView({
  status = 'form',
  email,
  onSubmit,
  onBackToLogin,
  onRequestNewLink,
  loading = false,
  initialValues = { new_password: '', confirmPassword: '' },
  initialErrors,
  initialTouched,
}) {
  const footer = (
    <AuthFootnote>
      Remember your password?{' '}
      <button type="button" onClick={onBackToLogin} disabled={loading}>
        Sign in
      </button>
    </AuthFootnote>
  );

  if (status === 'validating') {
    return (
      <AuthShell
        title="Checking your link"
        subtitle="One moment while we confirm this reset link is still valid."
      >
        <AuthBanner tone="neutral">Confirming your reset link</AuthBanner>
      </AuthShell>
    );
  }

  if (status === 'invalid') {
    return (
      <AuthShell
        title="This link is no longer valid"
        subtitle="Reset links work once and they expire. Request a fresh one and it will be in your inbox in a moment."
        back={{ onClick: onBackToLogin, label: 'Back to sign in' }}
        footer={footer}
      >
        <AuthSubmit type="button" onClick={onRequestNewLink}>
          Request a new link
        </AuthSubmit>
      </AuthShell>
    );
  }

  if (status === 'success') {
    return (
      <AuthShell
        title="Password reset"
        subtitle="Your password has been changed. Sign in with the new one."
        footer={footer}
      >
        <AuthBanner tone="success">
          You are being taken to the sign-in screen. If nothing happens, use the button below.
        </AuthBanner>

        <AuthSubmit type="button" onClick={onBackToLogin}>
          Go to sign in
        </AuthSubmit>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Set a new password"
      subtitle={
        email
          ? `You are resetting the password for ${email}. Signing in on your other devices will need the new one.`
          : 'Signing in on your other devices will need the new password.'
      }
      back={{ onClick: onBackToLogin, label: 'Back to sign in' }}
      footer={footer}
    >
      <Formik
        initialValues={initialValues}
        initialErrors={initialErrors}
        initialTouched={initialTouched}
        validationSchema={resetPasswordValidationSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, errors, touched, values }) => {
          const busy = isSubmitting || loading;
          const shown = (name) => (touched[name] && errors[name] ? errors[name] : undefined);

          return (
            <Form noValidate>
              <AuthFields>
                <AuthField
                  label="New password"
                  error={shown('new_password')}
                  hint="At least 8 characters. Mixing case, numbers and a symbol is what actually makes it strong."
                >
                  <FormikField
                    as={AuthPasswordInput}
                    name="new_password"
                    placeholder="Enter your new password"
                    autoComplete="new-password"
                    disabled={busy}
                  />
                </AuthField>

                <AuthStrength value={values.new_password} />

                <AuthField label="Confirm new password" error={shown('confirmPassword')}>
                  <FormikField
                    as={AuthPasswordInput}
                    name="confirmPassword"
                    placeholder="Confirm your new password"
                    autoComplete="new-password"
                    disabled={busy}
                  />
                </AuthField>
              </AuthFields>

              <AuthSubmit loading={busy} loadingLabel="Saving new password">
                Save new password
              </AuthSubmit>

              <AuthHint>You will be sent back to the sign-in screen to use it.</AuthHint>
            </Form>
          );
        }}
      </Formik>
    </AuthShell>
  );
}

export default ResetPasswordView;